<template>
    <v-ons-page>
        <ons-toolbar class="main-toolbar branded">
            <div class="left">
                <a v-on:click="$router.go(-1)">
                    <svg class="icon icon-back" aria-hidden="true"></svg>
                </a>
            </div>
            <div class="center brand-toobar">
                <div class="outlet-navbar">
                    <span class="outlet-store-name">{{ outlet ? outlet.store_display_name : '' }}</span>
                </div>
            </div>
            <div class="right">

            </div>
        </ons-toolbar>

        <div class="content">

            <v-ons-progress-bar v-if="show_loading" indeterminate class="fixed"/>

            <div class="content-inner" v-show="outlet">

                <div class="main-header mb-20 mt-20">Checkout</div>

                <!-- Order total -->
                <v-ons-list class="order-total-container" v-show="outlet">
                    <v-ons-list-item>
                        <div class="center">Order total</div>
                        <div class="right">
                            <span class="item-price-text" v-if="outlet">
                                {{ filters.money(this.pay_amount, true, outlet.currency_symbol) }}
                            </span>
                        </div>
                    </v-ons-list-item>
                    <v-ons-list-item v-show="using_split_payment">
                        <div class="center">Remaining amount</div>
                        <div class="right">
                            <span class="item-price-text" v-if="outlet">
                                {{ filters.money(this.pay_amount_remaining, true, outlet.currency_symbol) }}
                            </span>
                        </div>
                    </v-ons-list-item>
                </v-ons-list>


                <!-- Voucher entry -->
                <v-ons-list class="section-h" v-show="supports_vouchers">
                    <div class="sub-header">Apply {{ voucher_label }}</div>
                    <span class="link-button" v-bind:class="{ 'link-button-disabled': this.show_loading } " v-show="show_voucher_entry == false"
                        v-on:click="showVoucherEntry" data-test-id="btn-voucher-show">Enter {{ voucher_label }} code</span>
                    <template v-if="show_voucher_entry">
                        <form v-on:submit.prevent="enterVoucher">
                            <v-ons-list-item class="list-item-twocolumn" v-show="applied_voucher == null">
                                <div class="center">
                                    <v-ons-input class="inp-coupon" v-bind:placeholder="`Enter ${voucher_label} code`"
                                                 float v-model="voucher_code" data-test-id="inp-voucher-code"/>
                                </div>
                                <div class="right">
                                    <gtc-button v-bind:disabled="show_loading || helpers.stringEmpty(voucher_code)"
                                                class="btn-action branded small"
                                                type="submit"
                                                data-test-id="btn-voucher-apply">
                                        Apply
                                    </gtc-button>
                                </div>
                            </v-ons-list-item>
                        </form>
                        <v-ons-list class="account-balance-container" v-if="applied_voucher != null">
                            <v-ons-list-item>
                                <div class="center">Available to spend</div>
                                <div class="right">
                                    <span class="item-price-text">
                                        {{ filters.money(applied_voucher.balance, true, outlet.currency_symbol) }}
                                    </span>
                                </div>
                            </v-ons-list-item>
                        </v-ons-list>
                        <v-ons-list-item v-if="applied_voucher != null">
                            <div class="center">{{ filters.capitalise(voucher_label) }} code: {{ applied_voucher.code }}</div>
                            <div class="right">
                                <gtc-button class="btn-action branded small" v-on:click="removeVoucher">Remove</gtc-button>
                            </div>
                        </v-ons-list-item>
                    </template>
                </v-ons-list>

                <!-- Voucher payment -->
                <v-ons-list v-show="pay_all_on_voucher" class="section-h">
                    <div class="sub-header">Pay with {{ voucher_label }}</div>
                    <div class="">Pay using your {{ voucher_label }} balance.</div>
                    <section class="section-vh">
                        <gtc-button data-test-id="btn-pay-voucher"
                                    modifier="large"
                                    v-on:click="submitVoucher"
                                    v-bind:disabled="checkout_loading || card_loading"
                                    v-bind:in_progress="checkout_loading && attempted_payment_type == 'VOUCHER'"
                                    class="btn-action branded"
                                    icon="far fa-gift-card">
                            Pay with {{ voucher_label }}
                        </gtc-button>
                    </section>
                </v-ons-list>

                <!-- Card payment -->
                <v-ons-list v-show="supports_card && can_pay_with_paypal == false"
                    class="section-h list-no-divider">
                    <div class="sub-header">Pay by card</div>
                    <v-ons-list-item v-show="show_card_container">
                        <div class="center list-item-block" v-show="using_saved_card == false">
                            <span v-if="card_load_error" class="warning-text">Card payment unavailable.</span>

                            <!-- Payment processor card widgets -->
                            <div id="stripe-card-container" ref="stripe_card_element" data-test-id="cnt-stripe-elements"></div>

                            <div class="sumup-card-container">
                                <div id="sumup-card"></div>
                            </div>

                            <!-- Saved card selector -->
                            <div class="center mt-30" v-if="card_save_available && has_user && show_loading == false">
                                <div class="mt-20">
                                    <v-ons-checkbox
                                        data-test-id="inp-card-save"
                                        v-model="card_extra.save_card"
                                        v-bind:disabled="stripe_payment_intent != null"
                                        class="pressable card-checkbox"/>
                                        Save card
                                </div>
                                <div v-if="card_extra.save_card">
                                    <v-ons-input class="mt-20" placeholder="Nickname (eg Main card, Company card)"
                                                 float name="nickname" v-model="card_extra.card_name" data-test-id="inp-card-name">
                                    </v-ons-input>
                                    <div class="mt-20" v-if="payment_cards.length > 0">
                                        <v-ons-checkbox
                                            v-model="card_extra.set_default" class="pressable card-checkbox"
                                            data-test-id="inp-card-default"/>
                                            Set as default card
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-ons-list-item>

                    <div v-if="card_save_available && using_saved_card" class="mt-20">
                        <v-ons-list-item>
                            <div class="center">
                                <span class="card-number">
                                    <span v-for="i in [1,2,3]" v-bind:key="i" class="card-dots">
                                        ••••
                                    </span>
                                    {{ selected_card.last_4 }}
                                </span>
                                <div class="card-name">
                                    <span v-if="helpers.stringEmpty(selected_card.name) == false">{{ selected_card.name }}, </span>
                                    <span>{{ selected_card.brand }}</span>
                                    <span> - {{ filters.cardDateFormat(selected_card.expires_at) }}</span>
                                </div>
                            </div>
                            <div class="right">
                                <span class="change-card-text pressable" data-test-id="btn-card-change"
                                      v-on:click="show_card_picker = true">
                                    Change
                                </span>
                            </div>
                        </v-ons-list-item>
                    </div>

                    <section class="section-vh">
                        <gtc-button data-test-id="btn-pay-card"
                                    modifier="large"
                                    v-bind:disabled="can_pay_by_card == false"
                                    v-bind:in_progress="checkout_loading && attempted_payment_type == 'CARD'"
                                    icon="fal fa-credit-card"
                                    v-on:click="submitCard"
                                    class="btn-action branded">
                            Pay by card
                        </gtc-button>

                        <div class="card-toggle" v-show="payment_cards.length > 0 && using_saved_card == false" v-on:click="using_saved_card = true">
                            <span class="link-button" data-test-id="btn-card-saved">Use a saved card</span>
                        </div>

                         <div class="card-toggle" v-on:click="using_saved_card = false" v-show="using_saved_card">
                             <span class="link-button" data-test-id="btn-card-new">Pay with a new card</span>
                        </div>
                    </section>
                </v-ons-list>

                <template v-if="outlet">

                    <!-- Paypal payment -->
                    <v-ons-list v-show="can_pay_with_paypal"  class="section-h list-no-divider">
                        <div class="sub-header">Pay with PayPal</div>
                        <div class="loading-container loading-container-paypal" v-show="paypal_loaded == false">
                            <span>Loading PayPal checkout</span>
                            <v-ons-progress-circular indeterminate class="loading-spinner"/>
                        </div>
                        <v-ons-list-item v-show="paypal_loaded" v-if="show_card_container">
                            <div class="center list-item-block">
                                <div class="paypal-container">
                                    <div id="paypal-button"></div>
                                </div>
                            </div>
                        </v-ons-list-item>
                    </v-ons-list>

                    <!-- Stripe Payment Request -->
                    <v-ons-list v-show="supports_card && supports_payment_request && pay_all_on_voucher == false" class="section-h">
                        <div class="sub-header">{{ helpers.paymentRequestName() }}</div>
                        <v-ons-list-item>
                            <div class="center">
                                <div class="btn-payment-request" ref="payment_request_element"></div>
                            </div>
                        </v-ons-list-item>
                    </v-ons-list>

                    <!-- Cash payment -->
                    <v-ons-list v-show="supports_cash" class="section-h">
                        <div class="sub-header">Pay with cash</div>
                        <div class="">Pay when your order is collected or delivered.</div>
                        <section class="section-vh">
                            <gtc-button data-test-id="btn-pay-cash"
                                        modifier="large"
                                        v-on:click="submitCash"
                                        v-bind:disabled="checkout_loading || card_loading"
                                        v-bind:in_progress="checkout_loading && attempted_payment_type == 'CASH'"
                                        class="btn-action branded"
                                        icon="fal fa-money-bill-wave">
                                Pay with cash
                            </gtc-button>
                        </section>
                    </v-ons-list>

                    <!-- Pay in person -->
                    <v-ons-list v-show="supports_pay_in_person" class="section-h">
                        <div class="sub-header">Pay in person</div>
                        <div class="">Pay when your order is collected or delivered.</div>
                        <section class="section-vh">
                                <gtc-button data-test-id="btn-pay-in-person"
                                            modifier="large"
                                            v-on:click="submitPayInPerson"
                                            v-bind:disabled="checkout_loading || card_loading"
                                            v-bind:in_progress="checkout_loading && attempted_payment_type == 'PAY_IN_PERSON'"
                                            class="btn-action branded"
                                            icon="fal fa-money-bill-wave">
                                    Pay in person
                                </gtc-button>
                        </section>
                    </v-ons-list>

                    <!-- Pay on account -->
                    <v-ons-list v-show="supports_pay_on_account && user_account.is_account_customer" class="section-h">
                        <div class="sub-header">Pay on Account</div>
                        <div class="">Pay using your account balance and credit.</div>
                        <v-ons-list class="account-balance-container">
                            <v-ons-list-item>
                                <div class="center">Available to spend</div>
                                <div class="right">
                                    <span class="item-price-text">
                                        {{ filters.money(user_account.account_balance, true, outlet.currency_symbol) }}
                                    </span>
                                </div>
                            </v-ons-list-item>
                        </v-ons-list>

                        <section class="section-vh">
                            <gtc-button data-test-id="btn-pay-account" modifier="large"
                                        v-on:click="submitPayOnAccount"
                                        v-bind:disabled="checkout_loading || can_pay_on_account == false"
                                        v-bind:in_progress="checkout_loading && attempted_payment_type == 'ACCOUNT'"
                                        class="btn-action branded"
                                        icon="fas fa-user">
                                Pay on account
                            </gtc-button>
                        </section>

                        <v-ons-list-item v-if="can_pay_on_account">
                            <div class="balance-text">
                                Your balance will be
                                {{filters.money(user_account.account_balance - pay_amount, true, outlet.currency_symbol)}}
                                after this order
                            </div>
                        </v-ons-list-item>
                    </v-ons-list>

                    <!-- Free order -->
                    <v-ons-list v-show="can_pay_for_free" class="section-h">
                        <div class="sub-header">Your order is free</div>
                        <section class="section-vh">
                                <gtc-button data-test-id="btn-pay-free"
                                            modifier="large"
                                            v-on:click="submitPayForFree"
                                            v-bind:disabled="checkout_loading || card_loading"
                                            v-bind:in_progress="checkout_loading && attempted_payment_type == 'FREE'"
                                            class="btn-action branded"
                                            icon="fal fa-money-bill-wave">
                                   Place order
                                </gtc-button>
                        </section>
                    </v-ons-list>

                </template>

            </div>

        </div>

        <v-ons-alert-dialog
                data-test-id="dlg-products-changed"
                title="Unable to checkout"
                v-bind:visible.sync="products_changed.show"
                v-bind:footer="{ 'Remove products': () => this.removeChangedProducts() }">
            {{ products_changed.message }}
            <ul>
                <li v-for="product in products_changed.products">
                    {{ product.product_name }} - {{ product.issue }}
                </li>
            </ul>
        </v-ons-alert-dialog>

        <v-ons-alert-dialog
                data-test-id="dlg-products-oos"
                title="Unable to checkout"
                v-bind:visible.sync="products_oos.show"
                v-bind:footer="{ 'Close': () => this.handleProductsOos() }">
            {{ products_oos.message }}
            <ul>
                <li v-for="product in products_oos.products">
                    {{ product.product_name }} - <strong>{{ product.existing_quantity }}</strong> in stock
                </li>
            </ul>
        </v-ons-alert-dialog>

        <!--Loading-->
        <v-ons-alert-dialog v-bind:visible="show_loading_message" data-test-id="dlg-checkout-loading">
            <section class="section-vh">
                <div class="loading-container">
                    <span>{{ this.loading_message }}</span> <v-ons-progress-circular indeterminate class="loading-spinner"/>
                </div>
            </section>
        </v-ons-alert-dialog>

        <!-- Validation -->
        <validation v-bind:show="validation.show"
            v-bind:errors="validation.errors"
            v-on:show="(show) => validation.show = show"/>

        <!-- Card selector -->
        <v-ons-alert-dialog title="Select payment card" v-bind:visible.sync="show_card_picker" data-test-id="dlg-card-picker">
            <v-ons-list class="section-h">
               <v-ons-list-item
                    v-for="card in payment_cards"
                    v-bind:key="card.id"
                    v-on:click="choosePaymentCard(card)"
                    class="pressable">
                    <div class="center">
                        <span class="card-number">
                            <span v-for="i in [1,2,3]" v-bind:key="i" class="card-dots">
                                ••••
                            </span>
                            {{ card.last_4 }}
                        </span>
                        <div class="card-name">
                            <span v-if="card.name">{{card.name}}, </span>
                            <span>{{card.brand}}</span>
                            <span> - {{filters.cardDateFormat(card.expires_at)}}</span>
                        </div>
                    </div>
                    <div class="right">
                        <span class="change-card-text"
                            v-show="selected_card.id == card.id">
                            Selected</span>
                    </div>
                </v-ons-list-item>
            </v-ons-list>
            <section class="section-vh">
                <gtc-button modifier="large" v-on:click="show_card_picker = false" class="secondary">
                    Go back
                </gtc-button>
            </section>
        </v-ons-alert-dialog>
      
    </v-ons-page>

</template>

<script>
    import { mapGetters, mapMutations, mapActions } from 'vuex'
    import moment from 'moment'

    import { default as WhenOutletLoadedMixin } from '../../mixins/whenoutletloaded'
    import ValidationComponent from '../fragments/validation'

    export default {

        mixins: [WhenOutletLoadedMixin],

        props: [],

        components : {
            validation: ValidationComponent
        },

        created() {

        },

        mounted() {

            // No menu_option, can't do anything here
            if (this.order.menu_option == null) {
                this.$router.replace( { name: 'Menu', params: { outlet_slug: this.$route.params.outlet_slug } })
                return
            }
        },

        beforeDestroy() {

            // Unmount SumUp card widget if populated.
            if (this.sumup && this.sumup_state != 'UNLOADED')
                this.sumup.unmount()

            // Unmount Stripe card
            if (this.stripe_card) {
                this.$nextTick(() => {
                    this.stripe_card.unmount()
                     console.log('stripe elements unmounted')
                })

            }

            this.clearTimers()

            this.clearPayPal()
        },

        data() {
            return {
                app: this.$root.$data.app,
                stripe: null,
                stripe_payment_intent: null,
                sumup: null,
                card: null,
                card_error: null,
                card_load_error: false,
                card_loading: false,
                checkout_data: null,
                checkout_loading: false,
                checkout_attempt: 1,
                supports_payment_request: false,
                products_changed: {
                    show: false,
                    products: [],
                    outlet: null,
                    message: '',
                },
                products_oos: {
                    show: false,
                    products: [],
                    message: '',
                },
                validation: {
                    show: false,
                    errors: [],
                },
                payment_request: null,
                sumup_checkout_id: null,
                sumup_state: 'UNLOADED', // UNLOADED, LOADING, LOADED, DONE
                loading_messages: [
                    'Processing order...',
                    'Please do not close the page until checkout is complete...',
                ],
                loading_message: '',
                loading_message_secondary_timer: null,
                loading_message_timer: null,
                show_loading_message: false,
                attempted_payment_type: null,
                paypal_loaded: false,
                payment_cards: [],
                selected_card: null,
                card_extra: {
                    save_card: false,
                    card_name: '',
                    set_preferred: false
                },
                using_saved_card: false,
                payment_cards_loading: false,
                show_card_picker: false,
                going_to_checkout: false,
                user_account: {
                    account_balance: null,
                    is_account_customer: false,
                },
                outlet_cleared_at: null,
                voucher_loading: false,
                show_voucher_entry: false,
                voucher_code: '',
                applied_voucher: null,
            }
        },

        methods : {

            ...mapMutations([
                'clearOrder',
                'setCoupon',
                'changeProductQuantity',
                'removeProducts',
                'setCheckoutInProgress',
            ]),

            ...mapActions([
                'refreshState',
            ]),

            // Load PayPal for current merchant
            loadPayPalScript(){

                this.paypalCheckout()
                    .then(checkout_id => {
                        return new Promise((resolve, reject) => {
                            this.checkout_loading = true
                            const script = document.createElement('script')
                            const conf = this.outlet.paypal_config
                            script.setAttribute('src',
                                `https://www.paypalobjects.com/api/checkout.js?client-id=${conf.partner_client_id}&merchant-id=${conf.paypal_merchant_id}`)
                            if (env.paypal_env != 'sandbox')
                                script.setAttribute('data-log-level', 'warn')
                            script.setAttribute('data-partner-attribution-id', conf.partner_attribution_id)
                            script.setAttribute('id', 'paypal-script')
                            script.async = true
                            script.onload = () => {
                                console.log(`PayPal script loaded for ${this.outlet.slug}`)
                                resolve()
                            }
                            document.head.appendChild(script)
                        })
                        .then(() => {
                            this.$nextTick(() => {
                                paypal.Button.render(
                                    {
                                        env: env.paypal_env,
                                        commit: true,
                                        locale: (navigator.language || navigator.userLanguage).replace('-', '_'),
                                        style: {
                                            shape: 'rect',
                                            size: 'responsive',
                                            label: 'checkout',
                                            tagline: 'false',
                                        },
                                        payment: () => {
                                            return checkout_id
                                        },
                                        onAuthorize: (data) => {
                                            this.checkout('CARD', data['orderID'])
                                        },
                                        onCancel: (data, actions) => {
                                            console.log('User cancelled checkout.')
                                        },
                                        onError: (error) => {
                                            this.app.logException(new Error('PayPal checkout button error.', { error }))
                                        },
                                    },
                                    '#paypal-button'
                                )
                                .then(() => {
                                    this.paypal_loaded = true
                                    this.checkout_loading = false
                                })
                                .catch(error => {
                                    this.app.logException(new Error('Failed to load PayPal button.', { error }))
                                })
                            })
                        })
                    })
                .catch(error => {
                    this.processCheckoutError(error)
                })
            },

            clearPayPal() {
                // Remove PayPal script
                const pps = document.getElementById('paypal-script')
                if (pps) {
                    pps.parentElement.removeChild(pps)
                    console.log(`PayPal script removed for ${this.outlet.slug}`)
                }

                // PayPal tag manager script
                const pps_tracking = document.getElementById('xo-pptm')
                if (pps_tracking)
                    pps_tracking.parentElement.removeChild(pps_tracking)

                // Remove PayPal button
                const ppd = document.getElementById('paypal-button')
                if (ppd)
                    ppd.innerHtml = ''
            },

            getPayPalCheckoutData() {
                const data = this.getCheckoutData('CARD')
                const page_url = window.location.href
                data.urls = {
                    return: page_url,
                    cancel: page_url,
                }
                data.meta_charge = this.order.meta_charge
                return data
            },

            paypalCheckout() {
                const data = this.getPayPalCheckoutData() 
                return new Promise((resolve, reject) => {
                    this.app.http.post('order/paypalcheckout', data)
                        .then(response => {
                            return resolve(response.data.checkout_id)
                        })
                        .catch(error => {
                            return reject(error)
                        })
                })
            },

            getStripePaymentIntent() {

                // Return a new payment intent or get the existing one if the payment method hasn't changed
                return new Promise((resolve, reject) => {

                    // Get checkout data
                    const data = this.getCheckoutData('CARD')

                    // If we have a payment intent already, update it (current payment method may have changed or been
                    // removed, eg if payment authorisation was not given)
                    if (this.stripe_payment_intent != null) {
                        const update_data = {
                            outlet_slug: this.outlet.slug,
                            payment_intent_id: this.stripe_payment_intent.payment_intent_id,
                            payment_card_id: data.payment_card_id,
                            order_local_id: this.order.local_id,
                        }
                        this.app.http.patch('order/stripepaymentintentupdate', update_data)
                            .then(response => {
                                this.stripe_payment_intent = response.data
                                resolve(response.data)
                            })
                            .catch(error => {
                                reject(error)
                            })
                    }
                    // Get a new payment intent
                    else {
                        this.app.http.post('order/stripepaymentintent', data)
                            .then(response => {
                                this.stripe_payment_intent = response.data
                                resolve(response.data)
                            })
                            .catch(error => {
                                reject(error)
                            })
                    }

                })
            },

            choosePaymentCard(card) {
                this.selected_card = card
                this.show_card_picker = false
            },

            whenOutletLoaded(){

                // If we're about to move to the checkout page, don't load anything
                if (this.going_to_checkout)
                    return

                this.app.trackFbEvent(
                    'PageView',
                    {
                        content_name: 'Payment',
                    }
                )

                this.clearPayPal()

                // Go to menu if nothing in basket
                if (this.order.form == null || this.order.hasItems == 0) {
                    this.$router.replace({name: 'Menu', params: {outlet_slug: this.outlet.slug}})
                    return
                }

                if (this.outlet.supports_account && this.user != null) {
                    this.app.http.get(`outlet/${this.outlet.slug}/user/account`)
                        .then(response => {
                            this.user_account = response.data
                        })
                        .catch(error => {
                            this.app.logException('Failed to load account payment data for user.', { error })
                        })
                }

                this.checkout_loading = true

                this.refreshState(true)
                    .then(() => {

                        // We'll perform extra logic to initiate the card payment forms beyond this point
                        if (this.outlet.supports_card == false || this.can_pay_for_free) {
                            this.checkout_loading = false
                            return
                        }

                        // Stripe logic
                        if (this.outlet.card_payment_method == 'STRIPE') {

                            // Check Stripe loaded
                            if ('Stripe' in window == false) {
                                this.app.logException(new Error('Stripe not defined.'))

                                this.$ons.notification.confirm(
                                    'Unable to load payment processor script - please refresh the page and try again.',
                                    { title: 'Payment error' , buttonLabels: ['Refresh page']}
                                ).then(result => {
                                    window.location.reload()
                                })

                                return
                            }

                            // Setup elements
                            const stripe_params = {}
                            if (this.outlet.stripe_charge_type == 'DIRECT') {
                                stripe_params.stripeAccount = this.outlet.stripe_account_id
                            }
                            this.stripe = Stripe(env.stripe_pk, stripe_params)
                            const elements = this.stripe.elements()

                            // Setup card input element
                            const style = {
                                base: {
                                    fontSize: '16px',
                                    color: "#1f1f21",
                                }
                            }

                            this.stripe_card = elements.create('card', { style })
                            this.$nextTick(() => {
                                this.stripe_card.mount(this.$refs['stripe_card_element'])
                                console.log('stripe elements mounted')
                            })

                            this.stripe_card.addEventListener('change', event => {
                                this.card_error = event.error ? event.error.message : null
                            })

                            // Setup payment request button
                            const payment_request = this.stripe.paymentRequest({
                                country: this.outlet.outlet_country,
                                currency: this.outlet.currency.toLowerCase(),
                                total: this.getPaymentRequestData(),
                            })

                            const payment_request_btn = elements.create('paymentRequestButton', {
                                paymentRequest: payment_request,
                            })

                            payment_request.canMakePayment()
                                .then(result => {
                                    if (result == false)
                                        return

                                    payment_request_btn.mount(this.$refs['payment_request_element'])
                                    this.supports_payment_request = true
                                })
                                .catch(error => {
                                    // Payment request not available, log error if it's not an IntegrationError
                                    // (eg not available in current environment)
                                    const should_log = error.name != 'IntegrationError'
                                    if (should_log) {
                                        this.app.logException(
                                            new Error('Stripe payment request initialisation error.'),
                                            {error},
                                            'warning'
                                        )
                                    }
                                })

                            payment_request.on('paymentmethod', (ev) => {
                                this.submitStripePaymentRequest(ev)
                            })

                            this.payment_request = payment_request
                            this.checkout_loading = false

                            // Get user's cards
                            if (this.user && this.card_save_available) {

                                this.payment_cards_loading = true

                                this.app.http.get('payment_cards')
                                    .then(response => {
                                        this.payment_cards = response.data.payment_cards
                                            .filter(c => c.supports_stripe)

                                        if (this.payment_cards.length > 0) {
                                            this.using_saved_card = true
                                            // First card is always the default
                                            this.selected_card = this.payment_cards[0]
                                        }
                                        this.payment_cards_loading = false
                                    })
                                    .catch(error => {
                                        this.payment_cards_loading = false
                                    })
                            }
                        }
                        // SumUp logic
                        else if (this.outlet.card_payment_method == 'SUMUP') {
                            // Load the SumUp card widget only if no other payment methods are available
                            if (this.outlet.supports_cash == false && this.outlet.supports_pay_in_person == false)
                                this.loadSumupCardWidget()
                            this.checkout_loading = false
                        }
                        // PayPal logic
                        else if (this.outlet.card_payment_method == 'PAYPAL') {
                            // Load paypal script if current client has paypal
                            const paypal_conf = this.outlet.paypal_config
                            if (paypal_conf && paypal_conf.paypal_merchant_id != null){
                                if (this.can_pay_for_free == false)
                                    this.loadPayPalScript()
                                else 
                                    this.checkout_loading = false
                            } else {
                                this.app.logException(new Error('No PayPal config or merchant ID'))
                            }
                        }
                    })
                    .catch((error) => {
                        this.app.logException(new Error('Payment initialisation error.'), { error })

                        this.$ons.notification.confirm(
                            'An error occurred when loading the payment page - please try again.',
                            { title: 'Payment error' , buttonLabels: ['Go back']}
                        ).then(result => {
                            this.$router.replace({ name: 'CreateOrder', params: { outlet_slug: this.outlet.slug } })
                        })
                    })
            },

            submitPayForFree() {
                this.checkout('FREE')
            },

            submitStripePaymentRequest(ev) {

                this.checkout_loading = true

                this.getStripePaymentIntent()
                    .then(data => {
                        try {
                            this.stripe.confirmCardPayment(
                                data.client_secret,
                                { payment_method: ev.paymentMethod.id },
                                { handleActions: false },
                            )
                            .then(confirm_result => {
                                if (confirm_result.error) {
                                    // Report to the browser that the payment failed, prompting it to
                                    // re-show the payment interface, or show an error message and close
                                    // the payment interface.
                                    ev.complete('fail')
                                    this.processStripePaymentError(confirm_result)
                                } else {
                                    // Report to the browser that the confirmation was successful, prompting
                                    // it to close the browser payment method collection interface.
                                    ev.complete('success')
                                    // Check if the PaymentIntent requires any actions and if so let Stripe.js
                                    // handle the flow. If using an API version older than "2019-02-11" instead
                                    // instead check for: `paymentIntent.status === "requires_source_action"`.
                                    if (confirm_result.paymentIntent.status === 'requires_source_action') {
                                        // Let Stripe.js handle the rest of the payment flow.
                                        this.stripe.confirmCardPayment(data.client_secret).then(confirm_result_2 => {
                                            if (confirm_result_2.error) {
                                                this.processStripePaymentError(confirm_result_2)
                                            } else {
                                                 this.checkout('CARD', data.payment_intent_id)
                                            }
                                        })
                                    } else {
                                         this.checkout('CARD', data.payment_intent_id)
                                    }
                                }
                            })
                            .catch(error => {
                                ev.complete('fail')
                                this.checkout_loading = false
                                this.processStripeTokenError(error)
                            })
                        } catch (error) {
                            ev.complete('fail')
                            this.checkout_loading = false
                            this.processStripeTokenError(error)
                        }
                    })
                    .catch(error => {
                        ev.complete('fail')
                        this.checkout_loading = false
                        this.processCheckoutError(error)
                    })
            },

            submitCard() {

                if (this.card_loading)
                    return

                this.attempted_payment_type = 'CARD'

                // Stripe
                if (this.outlet.card_payment_method == 'STRIPE') {
                    this.checkout_loading = true

                    this.getStripePaymentIntent()
                        .then(data => {

                            let stripe_response = null

                            try {
                                const stripe_confirm_card_params = {}
                                if (this.using_saved_card == false)
                                    stripe_confirm_card_params.payment_method = { card: this.stripe_card }

                                stripe_response = this.stripe.confirmCardPayment(
                                    data.client_secret,
                                    stripe_confirm_card_params,
                                )

                                stripe_response.then(result => {
                                        if (result.error) {
                                            this.processStripePaymentError(result)
                                        } else {
                                            this.checkout('CARD', data.payment_intent_id)
                                        }
                                    })
                                    .catch(error => {
                                        this.checkout_loading = false
                                        this.processStripeTokenError(error)
                                    })
                            } catch (error) {
                                this.checkout_loading = false
                                this.processStripeTokenError(error)
                            }
                        })
                        .catch(error => {
                            this.checkout_loading = false
                            this.processCheckoutError(error)
                        })
                }
                // SumUp
                else if (this.outlet.card_payment_method == 'SUMUP') {

                    // If SumUp not requested yet, load SumUp
                    if (this.sumup_state == 'UNLOADED') {
                        this.loadSumupCardWidget()
                    }
                    // Loaded, not attempted payment yet. Validate order then attempt payment.
                    else if (this.sumup_state == 'LOADED') {

                        this.checkout_loading = true

                        const data = this.getCheckoutData('CARD', null)
                        data.checkout = true

                        this.app.http.post('order/validate', data)
                            .then(response => {
                                this.sumup.submit()
                            })
                            .catch(error => {
                                this.checkout_loading = false
                                this.processCheckoutError(error)
                            })
                    }
                    // Paid but Goodeats checkout failed, so submit to backend again
                    else if (this.sumup_state == 'DONE') {
                        this.checkout('CARD', this.sumup_checkout_id)
                    }
                    // Widget is loading, do nothing
                    else if (this.sumup_state == 'LOADING') {

                    }
                }
            },

            submitCash(){
                this.checkout('CASH')
            },

            submitVoucher(){
                this.checkout('VOUCHER')
            },

            submitPayInPerson(){
                this.checkout('PAY_IN_PERSON')
            },

            submitPayOnAccount(){
                this.checkout('ACCOUNT')
            },

            loadSumupCardWidget(){

                this.sumup_state = 'LOADING'
                this.card_loading = true

                const data = this.getCheckoutData('CARD')

                this.app.http.post(`order/sumupcheckout`, data)
                    .then(response => {

                        const checkout_id = response.data.checkout_id

                        this.sumup = SumUpCard.mount({
                            id: 'sumup-card',
                            checkoutId: checkout_id,
                            showZipCode: true,
                            showSubmitButton: false,
                            onLoad: () => {
                                this.sumup_checkout_id = checkout_id
                                this.sumup_state = 'LOADED'
                                this.card_loading = false
                            },
                            onResponse: (type, body) => {

                                if (type == 'success') {

                                    // Paid
                                    if (body.status == 'PAID') {
                                        this.sumup_state = 'DONE'
                                        this.checkout('CARD', checkout_id)
                                        return
                                    }
                                    // Error
                                    else {
                                        this.app.logException(
                                            new Error('SumUp card widget success response with non-paid status'),
                                            { sumup_response: body }
                                        )
                                        this.$ons.notification.alert(
                                            'Unable to charge your card - please check the details and try again.',
                                            { title: 'Payment error' }
                                        )
                                    }
                                }
                                // Log errors
                                else if (type == 'error') {
                                    this.app.logException(
                                        new Error('SumUp card widget response error'),
                                        { sumup_response: body },
                                        'warning'
                                    )
                                }

                                // Disable card button when waiting for a response
                                this.checkout_loading = type == 'sent'
                            }
                        })
                    })
                    .catch(error => {

                        this.card_load_error = true
                        this.card_loading = false

                        this.processCheckoutError(error)
                    })
            },

            getCheckoutData(payment_type, token=null){

                const data = Object.assign({}, this.order.form)

                // Build array of payments. Create voucher entry (if applied) then another payment for the remaining
                // amount.
                const payments = []

                if (this.applied_voucher) {
                    payments.push({
                        type: 'VOUCHER',
                        amount: this.applied_voucher_amount,
                        voucher_code: this.applied_voucher.code,
                    })
                }

                if (payment_type == 'CARD') {

                    const card_payment = {
                        type: 'CARD',
                        amount: this.pay_amount_remaining,
                    }

                    if (this.outlet.card_payment_method == 'STRIPE') {
                        if (this.using_saved_card && this.selected_card)
                            card_payment.payment_card_id = this.selected_card.id

                        card_payment.stripe_payment_intent_id = token
                    }
                    else if (this.outlet.card_payment_method == 'SUMUP')
                        card_payment.sumup_checkout_id = token
                    else if (this.outlet.card_payment_method == 'PAYPAL')
                        card_payment.paypal_checkout_id = token

                    payments.push(card_payment)
                }

                if (payment_type == 'CASH') {
                    payments.push({
                        type: 'CASH',
                        amount: this.pay_amount_remaining,
                    })
                }

                if (payment_type == 'ACCOUNT') {
                    payments.push({
                        type: 'ACCOUNT',
                        amount: this.pay_amount_remaining,
                    })
                }

                data.payments = payments

                if (this.using_saved_card)
                    data.payment_card_id = this.selected_card.id

                if (this.card_extra.save_card)
                    data.card_extra = this.card_extra

                data.amount_paid = this.pay_amount_base
                if (payment_type == 'FREE')
                    data.amount_paid = 0

                if (this.order.previous_sale_id) {
                    data.previous_sale_id = this.order.previous_sale_id
                }

                data.device.supports_paymentrequest = this.supports_payment_request

                // Detect if the page is running in an iframe and record the URL.
                data.embed_url = this.app.frameParentUrl()

                // If we're just logged in due to a previous error,
                // don't send data to create the user again
                if (this.user)
                    delete data.user

                return data
            },

            processCheckoutError(error){

                if (this.app.errorIsHttp(error) == false) {
                    this.app.logException(
                        new Error('Could not complete checkout.'),
                        {error},
                        'warning'
                    )
                }

                // Has response
                if (error.response && error.response.data) {

                    // If there is a payment error that cannot be resolved without going to checkout, record that on
                    // this page so that we don't infinitely attempt to reload the outlet
                    const go_to_checkout = error.response.data.go_to_checkout == true
                    this.going_to_checkout = go_to_checkout

                    // Account verification
                    if (error.response.data.error == 'guest_unverified') {
                        this.$ons.notification.alert(
                            error.response.data.message,
                            {title: 'Email unverified'}
                        )
                    }
                    // Products changed
                    else if (error.response.data.error == 'products_changed') {
                        this.products_changed.message = error.response.data.message
                        this.products_changed.products = error.response.data.products_changed
                        this.products_changed.outlet = error.response.data.outlet
                        this.products_changed.show = true
                    }
                    // Products out of stock
                    else if (error.response.data.error == 'products_oos') {
                        this.products_oos.message = error.response.data.message
                        this.products_oos.products = error.response.data.products_oos
                        this.products_oos.outlet = error.response.data.outlet
                        this.products_oos.show = true
                    }
                    // Coupon changed
                    else if (error.response.data.error == 'coupon_invalid') {
                        this.setCoupon(null)
                        this.$ons.notification.alert(
                            error.response.data.message,
                            {title: 'Discount code invalid'}
                        )
                    }
                    // Promotions changed
                    else if (error.response.data.error == 'promotions_invalid') {
                        this.$ons.notification.alert(
                            error.response.data.message,
                            {title: 'Offers have changed'}
                        )
                    }
                    // Validation
                    else if (error.response.data.hasOwnProperty('errors')) {
                        this.validation.errors = Object.values(error.response.data.errors).map(e => e[0])
                        this.validation.show = true
                    }
                    // Other error
                    else {
                        // Go to checkout prompt
                        if (go_to_checkout) {
                            this.$ons.notification.confirm(
                                error.response.data.message,
                                { title: 'Checkout error' , buttonLabels: ['Review order']}
                            ).then(result => {
                                this.$router.replace({ name: 'CreateOrder', params: { outlet_slug: this.outlet.slug } })
                            })
                        }
                        // Error alert
                        else {
                            this.$ons.notification.alert(
                                error.response.data.message,
                                { title: 'Checkout error' }
                            )
                        }
                    }
                    // Refresh basket if any of the above errors require it
                    if (error.response.data.refresh_checkout == true) {
                        this.refreshState(true)
                    }
                }
                // Other error
                else {
                    this.$router.push({ name: 'CreateOrder' })

                    let msg = 'An error occurred when placing your order.'
                    // Disable for now as non-capture is not enabled for everyone yet
                    //if (this.attempted_payment_type != 'CARD' || this.outlet.card_payment_method != 'SUMUP')
                    //    msg += ' Please try again, you will only be charged once.'

                    this.$ons.notification.alert(
                        msg,
                        { title: 'Checkout error' }
                    )
                }
            },

            processStripePaymentError(result) {
                this.checkout_loading = false

                // Get error details
                let message = result.error.message
                let error_code = result.error.code
                let error_type = result.error.type
                const zip_code_name = ['GB', 'IE'].includes(this.outlet.outlet_country) ? 'postcode' : 'zip code'

                // Log error. Blacklist error types which are expected (eg invalid card details provided).

                // Error connecting to Stripe from Stripe SDK
                const is_api_connection_error = error_type == 'api_connection_error'

                // Root error types
                const dont_log_error_types = [
                    // Card details invalid
                    'validation_error',
                    // Issue charging customer's card
                    'card_error',
                ]

                // Either no type specified, or type=invalid_request_error
                const dont_log_error_codes = [
                    // Issue charging customer's card
                    'card_decline_rate_limit_exceeded',
                    // 3DS failure
                    'payment_intent_authentication_failure',
                ]

                // Log if the error type and code does not appear in our blacklist
                const should_log = dont_log_error_types.includes(error_type) == false &&
                    dont_log_error_codes.includes(error_code) == false

                if (should_log) {
                    // API connection errors are more of a warning, log these separately
                    if (is_api_connection_error) {
                        this.app.logException(
                            new Error('Stripe API connection error'),
                            { stripe_error: result.error },
                            'warning',
                        )
                    }
                    // Log everything else as an error
                    else {
                        this.app.logException(
                            new Error('Stripe checkout failure'),
                            { stripe_error: result.error },
                            'error',
                        )
                    }
                }

                // Build error message
                // Use a clearer error message when failure is due to incorrect postcode
                if (error_code == 'incorrect_zip') {
                    message = `The ${zip_code_name} you supplied failed validation. Please ensure you are using the correct billing ${zip_code_name} for this card.`
                }

                // Use a clearer error message when failure is due to incomplete postcode
                if (error_code == 'incomplete_zip') {
                    message = `The ${zip_code_name} for the payment card is incomplete. Please enter the full billing ${zip_code_name} for this card in the form above.`
                }

                // If the card payment is blocked, add a message stating that it may still appear on the statement
                const is_card_error = result.error.type == 'card_error'
                if (is_card_error) {
                    const card_message = `
                    <strong>
                        <i class="fas fa-exclamation"></i>
                        This payment attempt may appear as a completed transaction on your statement
                        for several hours even though you have not been charged.
                    </strong>`
                    message += `<br><br>` + card_message
                }

                this.$ons.notification.alert({
                    title: 'Payment error',
                    messageHTML: message,
                })
            },

            processStripeTokenError(error) {
                this.checkout_loading = false

                let stripe_element_html = null
                try {
                    stripe_element_html = document.getElementById('stripe-card-container').innerHTML
                } catch (e) { }
                this.app.logException(new Error('Failed to create Stripe token.'), {
                    error,
                    stripe_element_html,
                })

                this.$ons.notification.confirm(
                    'An error occurred when taking the payment, your card has not been charged. Please re-enter your card details and try again.',
                    { title: 'Payment error' , buttonLabels: ['Try again']}
                ).then(result => {
                    this.$nextTick(() => {
                        this.stripe_card.unmount()
                        this.stripe_card.mount(this.$refs['stripe_card_element'])
                        console.log('remounted stripe card element')
                    })
                })
            },

            async beginCheckout() {

                this.setCheckoutInProgress(true)

                this.checkout_loading = true

                try {
                    let response = await this.checkoutRequest()
                    this.app.redirectToOrder(response)
                    return Promise.resolve(null)
                } catch (error) {

                    // No response received, need to retry
                    if (this.app.errorIsHttpWithoutResponse(error)) {

                        this.checkout_attempt += 1

                        // Reached max attempts, stop until the user chooses to continue.
                        // We need to subtract 1 from checkout_attempt because checkout_attempt is incremented _before_
                        // the attempt is made. Eg after 1 checkout attempt, the value is 2. We need to increment it
                        // before the attempt, because only attempts where no response is received are considered - real
                        // error responses from the server are expected and not considered. Therefore we need to adjust
                        // the value after each attempt.
                        if (this.app.maxRetryAttemptsReached(this.checkout_attempt - 1)) {
                            this.show_loading_message = false
                            this.checkout_loading = false

                            const attempts_exhausted_message = 'We were unable to checkout your order due to a network' +
                                ' connection issue. Please check your network connection, then click the retry checkout button.'
                            await this.$ons.notification.alert(
                                attempts_exhausted_message,
                                {
                                    title: 'Unable to checkout order',
                                    buttonLabel: 'Retry',
                                }
                            )
                        }

                        return await this.beginCheckout()
                    }
                    // Response received, handle expected errors
                    else {
                        this.setCheckoutInProgress(false)

                        this.show_loading_message = false
                        this.checkout_loading = false

                        this.processCheckoutError(error)
                        return Promise.resolve(null)
                    }
                }
            },

            async checkoutRequest() {

                this.clearTimers()
                this.loading_message = this.loading_messages[0]

                const checkout_request_failed = this.checkout_attempt > 1
                if (checkout_request_failed == false) {
                    this.show_loading_message = false

                    this.loading_message_timer = setTimeout(() => {
                        this.show_loading_message = true
                    }, 5000)

                    this.loading_message_secondary_timer = setTimeout(() => {
                        this.loading_message = this.loading_messages[1]
                    }, 10000)
                } else {
                    const network_message = 'A network error has occurred - we\'re retrying'
                    this.loading_message = network_message
                    this.show_loading_message = true

                    await new Promise(resolve => setTimeout(resolve, this.app.networkRetryDelay()))
                }

                try {
                    const config = { headers: { 'Request-Attempt': this.checkout_attempt } }
                    const response = await this.app.http.post('order/checkout', this.checkout_data, config)

                    this.app.analytics.track('checkout')

                    this.app.trackFbEvent(
                        'Purchase',
                        {
                            currency: this.outlet.currency,
                            value: this.pay_amount,
                        }
                    )

                    if (this.user) {
                        try {
                            await this.app.refresh()
                        } catch (error) {
                            this.app.logException(new Error('Failed to refresh after checkout'), {error})
                        }
                    }

                    this.clearOrder()

                    return Promise.resolve(response.data)

                } catch (error) {
                    this.clearTimers()
                    throw error
                }
            },

            checkout: async function(payment_type, token=null) {

                this.checkout_data = this.getCheckoutData(payment_type, token)
                return this.beginCheckout()
            },

            clearTimers(){
                if(this.loading_message_timer)
                    clearTimeout(this.loading_message_timer)
                if(this.loading_message_secondary_timer)
                    clearTimeout(this.loading_message_secondary_timer)
            },

            removeChangedProducts(){
                // Remove products
                this.removeProducts({
                    uuids: this.products_changed.products.map(e => e.uuid),
                    outlet: this.products_changed.outlet,
                })

                this.products_changed.show = false

                this.refreshState(true)

                this.$router.push({ name: 'CreateOrder' })
            },

            handleProductsOos(){

                this.products_oos.show = false
                this.$router.push({ name: 'CreateOrder' })
            },

            getPaymentRequestData(){

                const label = `${this.outlet.store_display_name} ` +
                    `${this.basketInfo.count} ` +
                    `${this.filters.pluralise(this.basketInfo.count, 'item', 'items')}`

                return {
                    label,
                    amount: this.pay_amount_remaining_base,
                }
            },

            showVoucherEntry() {

                if (this.show_loading) {
                    return
                }

                this.show_voucher_entry = true
            },

            enterVoucher() {

                this.voucher_loading = true
                const voucher_code = this.voucher_code
                this.app.http.get(`outlet/${this.outlet.slug}/voucher`, { voucher_code })
                    .then(response => {
                        this.voucher_loading = false
                        this.applied_voucher = {
                            code: voucher_code.toUpperCase(),
                            balance: response.data.balance,
                        }

                        // If we have a Stripe payment intent, eg due to previously failed payment, we need to clear it
                        // because the payment amount would have changed (due to voucher application) but the PI amount
                        // field is immutable. Clearing the ID will cause a new PI to be created when attempting the
                        // card payment, with the correct payment amount.
                        this.stripe_payment_intent = null
                    })
                    .catch(error => {
                        this.voucher_loading = false
                        this.applied_voucher = null
                        this.$ons.notification.alert(
                            this.app.getResponseErrorMessage(error),
                            {
                                title: `${this.filters.capitalise(this.voucher_label)} error`
                            },
                        )
                    })
            },

            removeVoucher() {
                this.applied_voucher = null

                // If we have a Stripe payment intent, eg due to previously failed payment, we need to clear it
                // because the payment amount would have changed (due to voucher application) but the PI amount
                // field is immutable. Clearing the ID will cause a new PI to be created when attempting the
                // card payment, with the correct payment amount.
                this.stripe_payment_intent = null
            },
        },

        computed: {
            ...mapGetters([
                'order',
                'basketInfo',
                'outlet',
                'user',
                'embed',
            ]),

            can_pay_on_account: function(){

                if (this.user_account.is_account_customer && this.supports_pay_on_account) 
                    return parseFloat(this.user_account.account_balance) >= this.pay_amount
                return false
            },

            can_pay_with_paypal: function(){
                return this.outlet != null &&
                    this.outlet.paypal_config != null &&
                    this.can_pay_for_free == false
            },
            
            has_user: function() {

                if (this.user) {
                    return true
                }

                if (this.order.checkout_info && this.order.checkout_info.user && this.order.checkout_info.user.is_guest == false) {
                    return true
                }

                return false
            },

            voucher_label: function(){
                if (this.outlet == null)
                    return ''

                return this.outlet.voucher_name.toLowerCase()
            },

            can_pay_by_card: function(){
                return this.card_error == null &&
                    this.card_load_error == false &&
                    this.show_loading == false &&
                    // Using a new card OR chosen an existing card
                    (this.using_saved_card == false || this.selected_card != null) &&
                    this.can_pay_for_free == false
            },

            can_pay_for_free: function(){
                return this.pay_amount_base <= 0
            },

            supports_card: function(){
                return this.outlet
                    && this.outlet.supports_card
                    && this.can_pay_for_free == false
                    && this.pay_all_on_voucher == false
            },

            supports_cash: function(){
                return this.outlet
                    && this.outlet.supports_cash
                    && this.can_pay_for_free == false
                    && this.pay_all_on_voucher == false
            },

            supports_vouchers: function() {
                return this.outlet
                    && this.outlet.supports_voucher
                    && this.can_pay_for_free == false
            },

            supports_pay_in_person: function(){
                return this.outlet
                    && this.outlet.supports_pay_in_person
                    && this.can_pay_for_free == false
                    && this.pay_all_on_voucher == false
                    && this.using_split_payment == false
                    && this.applied_voucher == null
            },

            supports_pay_on_account: function(){
                return this.outlet
                    && this.outlet.supports_account
                    && this.can_pay_for_free == false
                    && this.pay_all_on_voucher == false
                    && this.applied_voucher == null
            },

            show_loading: function(){
                return this.checkout_loading || this.card_loading || this.payment_cards_loading || this.voucher_loading
            },

            using_split_payment: function() {

                const pay_amount = currency(this.pay_amount)
                const pay_amount_remaining = currency(this.pay_amount_remaining)

                return pay_amount_remaining.intValue > 0 && pay_amount_remaining.intValue != pay_amount.intValue
            },

            applied_voucher_amount: function() {

                let voucher_amount = 0

                if (this.applied_voucher) {

                    const pay_amount = currency(this.pay_amount)
                    const balance_amount = currency(this.applied_voucher.balance)

                    // Use up to the balance of the voucher
                    if (balance_amount.intValue >= pay_amount.intValue) {
                        voucher_amount = pay_amount
                    } else {
                        voucher_amount = balance_amount
                    }
                }

                return currency(voucher_amount).toString()
            },

            voucher_balance_after_order: function(){

                if (this.applied_voucher != null) {
                    return this.applied_voucher.amount - this.applied_voucher_amount
                }

                return null
            },

            pay_amount: function(){
                return this.basketInfo.amount.total
            },

            pay_amount_base: function(){
                return currency(this.pay_amount).intValue
            },

            pay_amount_remaining: function() {
                return currency(this.pay_amount).subtract(this.applied_voucher_amount).toString()
            },

            pay_amount_remaining_base: function() {
                return currency(this.pay_amount_remaining).intValue
            },

            pay_all_on_voucher: function() {
                return this.applied_voucher != null && this.pay_amount_remaining == 0
            },

            show_card_container: function(){

                if (this.outlet == null)
                    return false

                if (this.outlet.card_payment_method == 'SUMUP')
                    return this.sumup_state != 'UNLOADED'

                return true
            },

            card_save_available: function(){

                return this.outlet &&
                    this.outlet.card_payment_method == 'STRIPE' &&
                    this.outlet.saved_card_enabled
            },
        },

        watch: {
            pay_amount_remaining_base: function(new_value, old_value){

                // Payment request not initialised
                if (this.payment_request == null)
                    return

                // The amount has changed to 0, likely because checkout has completed and the order has been reset
                if (new_value == 0)
                    return

                // Attempt to update Payment Request
                this.payment_request.update({ total: this.getPaymentRequestData() })
            },

            outlet: function(new_val, old_val) {
                if (new_val == null && old_val != null) {
                    this.outlet_cleared_at = moment().unix()
                }
            }
        },
    }
</script>

<style scoped>
    .loading-container-paypal {
        margin-top: 20px;
    }

    .list-item-block {
        display: block;
    }

    .btn-payment-request {
        max-width: var(--global-btn-max-width);
        border-radius: var(--global-border-radius);
        margin: 0 auto;
    }

    .sumup-card {
        width: 100%;
    }

    .sumup-card-container {
        max-width: 450px;
        margin: 0 auto;
    }

    .order-total-container {
        margin-bottom: 20px;
    }

    #paypal-button {
        width: 315px;
        max-width: 315px;
        margin: 0 auto;
        border-radius: 5px;
    }

    .paypal-container {
        padding: 0 20px;
        margin-top: 10px;
    }
    
    .card-toggle {
        margin-top: 20px;
    }

    .payment-card-btn {
        margin-top: 20px
    }

    .card-checkbox {
        margin-right: 5px; 
    }

    .change-card-text {
        font-size: 13px;
        margin-right: 10px;
        color: var(--global-goodtill-green);
        font-weight: bold;
    }

    account-balance-container {
        margin-bottom: 20px;
        margin-top: 5px;
    }

    .balance-text {
        margin: 0 auto;
    }
</style>


// WEBPACK FOOTER //
// src/js/components/order/pay.vue